import { useState, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Calendar } from 'lucide-react';
import api from '../lib/api';
import { messageHandler } from '../services/message-handler.service';
import type { Appointment, UserAccount } from '../types/api';
import { formatDate, formatDateTime } from '../lib/formatters';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import AppointmentForm from '../components/appointments/AppointmentForm';
import AppointmentOccurrences from '../components/appointments/AppointmentOccurrences';

type ModalType = 'create' | 'edit' | 'occurrences' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

const appointmentStatus = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  canceled: 'Cancelado',
  in_progress: 'Em Andamento',
  completed: 'Concluído',
  expired: 'Expirado',
  rescheduled: 'Reagendado',
} as const;

type AppointmentStatus = keyof typeof appointmentStatus;

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'confirmed':
    case 'in_progress':
      return 'info';
    case 'pending':
    case 'rescheduled':
      return 'warning';
    case 'canceled':
    case 'expired':
      return 'error';
    default:
      return 'info';
  }
};

function Appointments() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [reminderMinutes, setReminderMinutes] = useState<number[]>([30]);
  const queryClient = useQueryClient();

  const { data: appointments } = useQuery({
    queryKey: ['appointments'],
    queryFn: async () => {
      const response = await api.get('/appointments');
      return response.data;
    },
  });

  const { data: userAccounts } = useQuery({
    queryKey: ['user-accounts'],
    queryFn: async () => {
      const response = await api.get('/users/accounts');
      return response.data;
    },
  });

  const { data: occurrences } = useQuery({
    queryKey: ['appointment-occurrences', editingAppointment?.id],
    queryFn: async () => {
      if (!editingAppointment) return [];
      const response = await api.get(`/appointments/occurrences`, {
        params: { appointment_id: editingAppointment.id },
      });
      return response.data;
    },
    enabled: !!editingAppointment && modal.type === 'occurrences',
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Appointment>) => {
      // Handle all-day events
      if (data.all_day) {
        const startDate = (data.start_time as string).split('T')[0];
        data.start_time = `${startDate}T00:00`;
        data.end_time = `${startDate}T23:59`;
      }
      // If end_time is not provided, use start_time
      else if (!data.end_time) {
        data.end_time = data.start_time;
      }

      const response = await api.post('/appointments', data);
      if (selectedUsers.length > 0) {
        await Promise.all(
          selectedUsers.map(userId =>
            api.post('/appointments/notifications', {
              appointment_id: response.data.id,
              user_account_id: userId,
            })
          )
        );
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      messageHandler.success('Compromisso criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Appointment) => {
      // Handle all-day events
      if (data.all_day) {
        const startDate = formatDate(data.start_time, 'iso');
        data.start_time = `${startDate}T00:00`;
        data.end_time = `${startDate}T23:59`;
      }
      // If end_time is not provided, use start_time
      else if (!data.end_time) {
        data.end_time = data.start_time;
      }

      await api.put(`/appointments/${data.id}`, data);
      const existingNotifications = await api.get(
        `/appointments/notifications?appointment_id=${data.id}`
      );
      await Promise.all(
        existingNotifications.data.map((notification: any) =>
          api.delete(`/appointments/notifications/${notification.id}`)
        )
      );
      await Promise.all(
        selectedUsers.map(userId =>
          api.post('/appointments/notifications', {
            appointment_id: data.id,
            user_account_id: userId,
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointment-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['appointment-occurrences'] });
      messageHandler.success('Compromisso atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/appointments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      messageHandler.success('Compromisso excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateOccurrenceMutation = useMutation({
    mutationFn: async (data: { id: number; status: string }) => {
      await api.put(`/appointments/occurrences/${data.id}`, { status: data.status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['appointment-occurrences', editingAppointment?.id],
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteOccurrenceMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/appointments/occurrences/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['appointment-occurrences', editingAppointment?.id],
      });
      messageHandler.success('Ocorrência excluída com sucesso!');
      setModal({ type: 'occurrences' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      location: formData.get('location') as string,
      start_time: formData.get('start_time') as string,
      end_time: (formData.get('end_time') as string) || (formData.get('start_time') as string),
      all_day: formData.get('all_day') === 'true',
      status: formData.get('status') as AppointmentStatus,
      recurrence_type: formData.get('recurrence_type') as
        | 'none'
        | 'daily'
        | 'weekly'
        | 'monthly'
        | 'yearly',
      reminder_minutes: reminderMinutes,
    };

    if (editingAppointment) {
      updateMutation.mutate({ ...editingAppointment, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (appointment: Appointment) => {
    messageHandler.confirm(
      'Excluir Compromisso',
      'Tem certeza que deseja excluir este compromisso?',
      () => deleteMutation.mutate(appointment.id)
    );
  };

  const handleDeleteOccurrence = (occurrence: any) => {
    messageHandler.confirm(
      'Excluir Ocorrência',
      'Tem certeza que deseja excluir esta ocorrência?',
      () => deleteOccurrenceMutation.mutate(occurrence.id)
    );
  };

  const handleEdit = (appointment: Appointment) => {
    if (appointment.status === 'canceled' || appointment.status === 'completed') {
      messageHandler.alert(
        `Compromisso ${appointment.status === 'canceled' ? 'Cancelado' : 'Concluído'}`,
        `Este compromisso está ${
          appointment.status === 'canceled' ? 'cancelado' : 'concluído'
        } e não pode ser editado. Por favor, crie um novo compromisso se necessário.`,
        'warning'
      );
      return;
    }

    setEditingAppointment(appointment);
    setReminderMinutes(appointment.reminder_minutes || [30]);
    api.get(`/appointments/notifications?appointment_id=${appointment.id}`).then(response => {
      setSelectedUsers(response.data.map((notification: any) => notification.user_account_id));
    });
    setModal({ type: 'edit' });
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingAppointment(null);
    setSelectedUsers([]);
    setReminderMinutes([30]);
  };

  const isAppointmentReadOnly = (status: string) => status === 'canceled' || status === 'completed';

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Compromissos"
          description="Lista de todos os compromissos e seus detalhes."
          actions={
            <Button
              onClick={() => {
                setEditingAppointment(null);
                setSelectedUsers([]);
                setReminderMinutes([30]);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Compromisso
            </Button>
          }
        />

        {appointments?.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Início</TableHead>
                  <TableHead>Fim</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="relative">
                    <span className="sr-only">Ações</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appointments.map((appointment: Appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell>{appointment.title}</TableCell>
                    <TableCell>{formatDateTime(appointment.start_time)}</TableCell>
                    <TableCell>{formatDateTime(appointment.end_time)}</TableCell>
                    <TableCell>{appointment.location}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(appointment.status)}>
                        {appointmentStatus[appointment.status as AppointmentStatus]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        {isAppointmentReadOnly(appointment.status) ? (
                          <>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setEditingAppointment(appointment);
                                setModal({ type: 'edit' });
                              }}
                              title="Visualizar"
                            >
                              <Calendar className="h-4 w-4 text-gray-400" />
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => handleDelete(appointment)}
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setEditingAppointment(appointment);
                                setModal({ type: 'occurrences' });
                              }}
                              title="Ocorrências"
                              disabled={appointment.recurrence_type === 'none'}
                            >
                              <Calendar className="h-4 w-4 text-primary-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => handleEdit(appointment)}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => handleDelete(appointment)}
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState
            icon={<Calendar className="h-12 w-12" />}
            title="Nenhum compromisso encontrado"
            description="Crie seu primeiro compromisso para começar"
            action={{
              label: 'Criar Compromisso',
              onClick: () => {
                setEditingAppointment(null);
                setSelectedUsers([]);
                setReminderMinutes([30]);
                setModal({ type: 'create' });
              },
            }}
          />
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={
            editingAppointment
              ? isAppointmentReadOnly(editingAppointment.status)
                ? 'Visualizar Compromisso'
                : 'Editar Compromisso'
              : 'Novo Compromisso'
          }
          size="xl"
        >
          <AppointmentForm
            appointment={editingAppointment}
            userAccounts={userAccounts}
            selectedUsers={selectedUsers}
            onSelectedUsersChange={setSelectedUsers}
            reminderMinutes={reminderMinutes}
            onReminderMinutesChange={setReminderMinutes}
            onSubmit={handleSubmit}
            onClose={closeModal}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </Modal>

        {/* Occurrences Modal */}
        <Modal
          isOpen={modal.type === 'occurrences'}
          onClose={closeModal}
          title={`Ocorrências - ${editingAppointment?.title}`}
          size="xl"
        >
          <AppointmentOccurrences
            occurrences={occurrences}
            onStatusChange={(id, status) => {
              updateOccurrenceMutation.mutate({ id, status });
            }}
            onDelete={handleDeleteOccurrence}
            isUpdating={updateOccurrenceMutation.isPending}
          />
          <div className="mt-5">
            <Button type="button" variant="secondary" onClick={closeModal} fullWidth>
              Fechar
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  );
}

export default Appointments;
